import {findIndex} from 'lodash';

import type * as PanelTypes from '../../state/views/panel/types';
import {getColumnWidth} from '../../util/panelbankFlow';
import {PanelBankFlowSectionConfig} from './types';

// Returns the panel index of the current drop target
export const getDropIndex = ({
  boxHeight,
  boxWidth,
  gutterWidth,
  panelsPerRow,
  xPx,
  yPx,
}: {
  boxHeight: number;
  boxWidth: number;
  gutterWidth: number;
  panelsPerRow: number;
  xPx: number;
  yPx: number;
}) => {
  const x = Math.floor(xPx / (boxWidth + gutterWidth));
  const y = Math.floor(yPx / (boxHeight + gutterWidth));
  return x + y * panelsPerRow;
};

const getFlowSectionColumnWidth = ({
  columnCount,
  flowConfig,
  panelBankWidth,
}: {
  columnCount?: number;
  flowConfig: PanelBankFlowSectionConfig;
  panelBankWidth: number;
}) => getColumnWidth(panelBankWidth, flowConfig, columnCount);

// Given a px width, returns the closest number of columns to accomodate that width.
// (Enables 'snap to columns' behavior when resizing panels.)
export const getClosestColumnCount = ({
  flowConfig,
  panelBankWidth,
  pxWidth,
}: {
  flowConfig: PanelBankFlowSectionConfig;
  panelBankWidth: number;
  pxWidth: number;
}) => {
  const snapWidths = [1, 2, 3, 4, 5, 6, 7, 8].map(item =>
    getFlowSectionColumnWidth({panelBankWidth, flowConfig, columnCount: item})
  );
  let closestIndex = 0;
  snapWidths.forEach((columnWidth, i) => {
    if (
      Math.abs(pxWidth - columnWidth) <
      Math.abs(pxWidth - snapWidths[closestIndex])
    ) {
      closestIndex = i;
    }
  });
  return closestIndex + 1;
};

export const getSnappedWidth = ({
  flowConfig,
  panelBankWidth,
  snapToColumns,
  w,
}: {
  flowConfig: PanelBankFlowSectionConfig;
  panelBankWidth: number;
  snapToColumns: PanelBankFlowSectionConfig['snapToColumns'];
  w: number;
}) => {
  if (snapToColumns == null) {
    return w;
  }
  const newColumnCount = getClosestColumnCount({
    panelBankWidth,
    flowConfig,
    pxWidth: w,
  });
  return getFlowSectionColumnWidth({
    panelBankWidth,
    flowConfig,
    columnCount: newColumnCount,
  });
};

// Returns [x,y] px position for a box at the given index
export const getBoxPositionFromIndex = ({
  boxPositionIndex,
  boxWidth,
  boxHeight,
  gutterWidth,
  panelsPerRow,
}: {
  boxPositionIndex: number;
  boxWidth: number;
  boxHeight: number;
  gutterWidth: number;
  panelsPerRow: number;
}) => {
  return [
    gutterWidth + (boxPositionIndex % panelsPerRow) * (boxWidth + gutterWidth),
    Math.floor(boxPositionIndex / panelsPerRow) * (boxHeight + gutterWidth),
  ];
};

const getBoxPositionIndexWhileDragging = ({
  currentBoxPositionIndex,
  draggingWithinSection,
  dragIndex,
  dropIndex,
  numPanelsInSection,
  selectedForDrag,
}: {
  currentBoxPositionIndex: number;
  draggingWithinSection: boolean;
  dragIndex: number;
  dropIndex: number;
  numPanelsInSection: number;
  selectedForDrag: boolean;
}) => {
  if (dragIndex == null) {
    return currentBoxPositionIndex;
  }

  if (selectedForDrag) {
    return Math.min(dropIndex, numPanelsInSection - 1);
  }

  if (draggingWithinSection) {
    // dragging a panel from the same section to a lower index
    if (
      currentBoxPositionIndex >= dropIndex &&
      currentBoxPositionIndex <= dragIndex
    ) {
      return currentBoxPositionIndex + 1;
    }
    if (
      // dragging a panel from the same section to a higher index
      currentBoxPositionIndex <= dropIndex &&
      currentBoxPositionIndex > dragIndex
    ) {
      return currentBoxPositionIndex - 1;
    }
  }

  if (
    // dragging a panel from a different section
    !draggingWithinSection &&
    currentBoxPositionIndex >= dropIndex
  ) {
    return currentBoxPositionIndex + 1;
  }

  return currentBoxPositionIndex;
};

export const getPanelBoxPosition = ({
  boxHeight,
  boxWidth,
  draggingWithinSection,
  dragIndex,
  dropIndex,
  gutterWidth,
  panelRefId,
  panelRefsToRender,
  panelsPerRow,
  selectedForDrag,
}: {
  boxHeight: number;
  boxWidth: number;
  draggingWithinSection: boolean;
  dragIndex: number;
  dropIndex: number;
  gutterWidth: number;
  panelRefId: string;
  panelRefsToRender: (PanelTypes.Ref | false)[];
  panelsPerRow: number;
  selectedForDrag: boolean;
}) => {
  const currentBoxPositionIndex = findIndex(panelRefsToRender, {
    id: panelRefId,
  });
  const boxPositionIdxWhileDragging = getBoxPositionIndexWhileDragging({
    currentBoxPositionIndex,
    draggingWithinSection,
    dragIndex,
    dropIndex,
    numPanelsInSection: panelRefsToRender.length,
    selectedForDrag,
  });

  const currBoxPosition = getBoxPositionFromIndex({
    boxPositionIndex: currentBoxPositionIndex,
    boxWidth,
    boxHeight,
    gutterWidth,
    panelsPerRow,
  });
  const newBoxPosition = getBoxPositionFromIndex({
    boxPositionIndex: boxPositionIdxWhileDragging,
    boxWidth,
    boxHeight,
    gutterWidth,
    panelsPerRow,
  });

  return {
    boxPositionIdxWhileDragging,
    currBoxPosition,
    newBoxPosition,
  };
};
